/*
 * Ceny z prawdziwych ofert: model kalkulatora z artykułu „Planujesz fotowoltaikę w 2027 roku?” i ocena zakresu ceny
 * zlecenia dla firmy na Rynku (prośba właściciela 2026-11-02). Jedno źródło liczb dla obu miejsc.
 *
 * Model z VV/dane_badawcze/kalibracja_widelek_2026-10-29/ceny_do_artykulu.py (klucz `kalkulator` w ceny_do_artykulu.json):
 * środkowa cena to prosta od kWp (i kWh przy magazynie) dopasowana regresją kwantylową do ofert bez sprzętu premium,
 * zakres to środkowa połowa odchyleń ofert od tej prostej, premium to mediana ilorazu cen ofert premium do prostej.
 */
const MODEL = {
  mag: { stala: 18359, zaKwp: 1376, zaKwh: 488, dol: 0.912, gora: 1.069, premium: 1.311 },
  pv: { stala: 8359, zaKwp: 1282, dol: 0.925, gora: 1.318 },
}
// [kWp, kWh] ofert z magazynem i kWp ofert bez magazynu: do liczenia, ile ofert w danych jest podobnych do wybranej.
const PUNKTY_MAG = [[4.1,16],[4.8,12],[4.8,13],[4.8,15],[4.8,15],[5.0,5],[5.0,5],[5.0,9],[5.0,10],[5.0,10],[5.0,12],[5.0,12],[5.0,15],[5.0,16],[5.0,16],[5.0,16],[5.4,12],[5.4,12],[5.5,5],[5.5,12],[5.5,16],[5.8,5],[6.0,5],[6.0,5],[6.0,11],[6.0,15],[6.0,16],[6.0,16],[6.0,16],[6.1,16],[6.3,5],[6.5,16],[6.7,5],[6.9,10],[6.9,12],[6.9,18],[6.9,27],[7.0,15],[7.0,15],[7.2,16],[7.5,15],[7.5,15],[8.0,10],[8.0,15],[8.0,16],[8.0,16],[8.1,5],[8.3,16],[8.5,18],[8.5,23],[8.6,5],[8.6,18],[8.6,23],[9.0,20],[9.0,20],[9.0,18],[9.0,18],[9.1,18],[9.2,16],[9.5,30],[9.6,5],[9.7,5],[9.8,16],[10.0,9],[10.0,9],[10.0,10],[10.0,15],[10.0,15],[10.0,15],[10.0,16],[10.0,16],[10.0,16],[10.0,20],[10.0,20],[10.0,20],[10.0,20],[10.0,20],[10.0,20],[10.0,23],[10.0,23],[10.0,24],[10.0,24],[10.0,24],[10.0,27],[10.0,29],[10.0,30],[10.0,30],[10.0,30],[10.0,30],[10.0,15],[10.0,30],[10.1,30],[10.1,30],[10.2,24],[10.3,24],[10.4,30],[10.4,31],[10.5,16],[10.8,23],[10.8,27],[11.0,28],[12.0,16],[12.0,28],[12.0,27],[12.0,30],[12.1,26],[14.0,29],[15.0,30],[15.0,30],[15.0,30],[15.2,20],[17.0,21],[19.8,20],[20.0,40]]
const PUNKTY_PV = [3.2,3.6,4.4,5.0,5.0,5.8,6.0,6.1,6.3,6.7,6.9,7.0,7.0,7.2,7.7,8.0,8.3,9.2,9.2,9.6,9.6,9.6,9.8,9.8,9.9,9.9,9.9,9.9,10.0,10.0,10.0,10.0,10.0,10.0,10.0,10.0,10.0,12.0,15.0,15.2,19.8,20.0]

const do500 = (n) => Math.round(n / 500) * 500

export const policzCene = (kwp, kwh, premium) => {
  const m = kwh > 0 ? MODEL.mag : MODEL.pv
  const srodek = (m.stala + m.zaKwp * kwp + (kwh > 0 ? m.zaKwh * kwh : 0)) * (kwh > 0 && premium ? m.premium : 1)
  const podobne = kwh > 0
    ? PUNKTY_MAG.filter(([p, e]) => Math.abs(p - kwp) <= 2 && Math.abs(e - kwh) <= 6).length
    : PUNKTY_PV.filter((p) => Math.abs(p - kwp) <= 2).length
  return { od: do500(srodek * m.dol), srodek: do500(srodek), do: do500(srodek * m.gora), podobne }
}

// Marki premium jak przy liczeniu modelu (`kalibruj.PREMIUM`): oferty na nich są o ok. 31% droższe od prostej.
const PREMIUM = /sigen|fronius|huawei|solaredge|goodwe esa|enphase|tesla/i
// Poniżej tylu podobnych ofert w danych nie oceniamy: model zgadywałby poza tym, co widział.
const MIN_PODOBNYCH = 3
const MOC_MODELU = { od: 3, do: 20 }

const liczbaZOpisu = (v) => {
  const n = Number(String(v ?? '').replace(',', '.'))
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * Jak zakres ceny zlecenia wypada wobec prawdziwych ofert: { poziom: 'powyzej' | 'typowo' | 'ponizej', od, do }, gdzie
 * od i do to środkowa połowa podobnych ofert. Porównujemy środek zakresu klienta.
 *
 * Null, gdy porównanie nic by nie znaczyło: klient kupuje sprzęt sam (zakres to sam montaż), zlecenie to nie cała
 * instalacja (sam magazyn, rozbudowa), brak mocy albo pojemności, za mało podobnych ofert. Brak danych nie karze zlecenia.
 * Null także przy zakresie z naszego licznika (`zwykle_ceny`, klient nie podał budżetu): to nie oczekiwanie klienta, a licznik
 * rozjeżdża się z ofertami (przy 5 kWp z 12 kWh o ok. 35% niżej), więc firma widziałaby czerwony znak przez nasz rachunek.
 * Do zdjęcia, gdy licznik i oferty będą liczone jednakowo (decyzja właściciela 2026-11-02: ujednolicić później).
 */
export const ocenaZakresu = (o, zakres) => {
  if (!zakres?.od || !zakres?.do || o.kto_kupuje === 'klient' || o.sprzet === 'kupie_sam') return null
  if (o.zakres_zrodlo === 'zwykle_ceny') return null
  if (!['pv', 'pv_magazyn'].includes(o.typ)) return null
  const opis = o.opis || {}
  const kwp = liczbaZOpisu(opis.kwp)
  if (!kwp || kwp < MOC_MODELU.od || kwp > MOC_MODELU.do) return null
  const kwh = o.typ === 'pv_magazyn' ? liczbaZOpisu(opis.magazyn_kwh) : 0
  if (kwh === null) return null
  const premium = PREMIUM.test([opis.falownik, opis.falownik_marka, opis.magazyn, opis.magazyn_marka].filter(Boolean).join(' '))
  const rynek = policzCene(kwp, kwh, premium)
  if (rynek.podobne < MIN_PODOBNYCH) return null
  const srodek = (zakres.od + zakres.do) / 2
  const poziom = srodek > rynek.do ? 'powyzej' : srodek < rynek.od ? 'ponizej' : 'typowo'
  return { poziom, od: rynek.od, do: rynek.do }
}
